import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { candidateClient } from "../../client/candidateClient";

const SmsVerificationModal = ({ data, onVerified, open, onClose, smsId }) => {
  const { t } = useTranslation();
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(120);

  useEffect(() => {
    if (open) handleResendSms();
  }, [open]);

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerification = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error("Lütfen geçerli bir doğrulama kodu giriniz");
      return;
    }

    setIsLoading(true);
    try {
      const response = await candidateClient.post(
        `verify/${smsId}?token=${verificationCode}&phoneNumber=${data.ceP_TEL_NO.replaceAll(
          " ",
          ""
        )}`
      );

      if (response.data.success) {
        onVerified();
        toast.success(response.data.message || "Doğrulama başarılı");
        // Handle successful verification
        onClose(); // Close modal on success
      } else {
        // Handle error cases
        if (response.data.message) {
          toast.error(response.data.message);
        }
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach((error) => toast.error(error));
        }
      }
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData) {
        if (responseData.message) {
          toast.error(responseData.message);
        }
        if (responseData.errors && responseData.errors.length > 0) {
          responseData.errors.forEach((error) => toast.error(error));
        }
      } else {
        toast.error(t("sms_verfy_error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendSms = async () => {
    if (resendDisabled) return;
    setIsLoading(true);
    setResendDisabled(true);
    try {
      const response = await candidateClient.post(`send-sms/${smsId}`, {
        ...data,
        ceP_TEL_NO: data.ceP_TEL_NO?.replaceAll(" ", ""),
      });

      if (response.data.success) {
        toast.success(response.data.message || "SMS yeniden gönderildi");
        setCountdown(120); // 2 minutes countdown
      } else {
        // Handle error cases
        if (response.data.message) {
          toast.error(response.data.message);
        }
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach((error) => toast.error(error));
        }
        setResendDisabled(false);
      }
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData) {
        if (responseData.errors.length === 0 && responseData.message) {
          toast.error(responseData.message);
        }
        if (responseData.errors && responseData.errors.length > 0) {
          responseData.errors.forEach((error) => toast.error(error));
        }
      } else {
        toast.error(t("sms_send_error"));
      }
      setResendDisabled(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("sms_verify_title")}
        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom align="center">
          {t("sms_verfy_warning", { phone: data.ceP_TEL_NO })}
        </Typography>

        <Box sx={{ mt: 3, mb: 3 }}>
          <TextField
            fullWidth
            label={t("sms_verify_code")}
            variant="outlined"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder={t("enter_to_verfy_code")}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 2, flexDirection: { xs: "column", sm: "row" }, gap: 1 }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleVerification}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("verify")
          )}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleResendSms}
          disabled={resendDisabled || isLoading}
        >
          {countdown > 0
            ? `${t("re_send_sms")} (${countdown}s)`
            : t("re_send_sms")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SmsVerificationModal;
